import { motion, useMotionValue, useTransform, type PanInfo } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import type { WorkoutCard } from '../../lib/workoutCards';
import { WorkoutCardRenderer } from './WorkoutCardRenderer';
import { hapticMedium, hapticLight } from '../../lib/haptics';

interface CardNavigatorProps {
  cards: WorkoutCard[];
  currentIndex: number;
  isPlaying: boolean;
  ttsEnabled?: boolean;
  restTimerStates: Record<string, number>;
  onNavigate: (index: number) => void;
  onTogglePlay: () => void;
  onToggleTts?: () => void;
  onSetUpdate: (cardId: string, field: 'weight' | 'repsDone' | 'notes', value: string) => void;
  onRestTimeUpdate: (cardId: string, seconds: number) => void;
  onSkipExercise?: (itemId: string, reason?: string) => void;
  onOpenSwap?: (itemId: string) => void;
  onCancel?: () => void;
}

export function CardNavigator({
  cards,
  currentIndex,
  isPlaying,
  ttsEnabled = false,
  restTimerStates,
  onNavigate,
  onTogglePlay,
  onSetUpdate,
  onRestTimeUpdate,
  onSkipExercise,
  onOpenSwap,
  onCancel,
  onToggleTts,
}: CardNavigatorProps) {
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [entryDirection, setEntryDirection] = useState<'left' | 'right' | null>(null);
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-200, 0, 200], [0.5, 1, 0.5]);
  const rotate = useTransform(x, [-200, 0, 200], [-10, 0, 10]);

  const currentCard = cards[currentIndex];

  // Countdown timer state for autoplay
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoDuration = currentCard?.autoDuration || 0;
  const canAutoAdvance = currentCard?.type !== 'set';

  // Reset elapsed time when card changes
  useEffect(() => {
    setElapsedSeconds(0);
  }, [currentIndex]);

  // Track elapsed time when playing
  useEffect(() => {
    if (isPlaying && autoDuration > 0 && canAutoAdvance) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => {
          if (prev >= autoDuration) {
            return prev;
          }
          return prev + 0.1;
        });
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, autoDuration, canAutoAdvance]);

  // Calculate progress for circular timer (1 = full, 0 = empty)
  const progress = autoDuration > 0 && canAutoAdvance ? Math.max(0, 1 - elapsedSeconds / autoDuration) : 0;
  const showCountdown = isPlaying && autoDuration > 0 && canAutoAdvance;

  // Find next/prev non-skipped card indices
  const findNextNonSkipped = (fromIndex: number): number | null => {
    for (let i = fromIndex + 1; i < cards.length; i++) {
      if (!cards[i].isSkipped) return i;
    }
    return null;
  };

  const findPrevNonSkipped = (fromIndex: number): number | null => {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (!cards[i].isSkipped) return i;
    }
    return null;
  };

  const nextNonSkippedIndex = findNextNonSkipped(currentIndex);
  const prevNonSkippedIndex = findPrevNonSkipped(currentIndex);
  const canGoPrev = prevNonSkippedIndex !== null;
  const canGoNext = nextNonSkippedIndex !== null;

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 100;
    const velocity = info.velocity.x;

    if (Math.abs(info.offset.x) > threshold || Math.abs(velocity) > 500) {
      if (info.offset.x > 0 && canGoPrev && prevNonSkippedIndex !== null) {
        // Swipe right - go to previous non-skipped (next card enters from left)
        hapticLight();
        setSwipeDirection('right');
        setEntryDirection('left');
        setTimeout(() => {
          onNavigate(prevNonSkippedIndex);
          setSwipeDirection(null);
          setTimeout(() => setEntryDirection(null), 50);
        }, 200);
      } else if (info.offset.x < 0 && canGoNext && nextNonSkippedIndex !== null) {
        // Swipe left - go to next non-skipped (next card enters from right)
        hapticLight();
        setSwipeDirection('left');
        setEntryDirection('right');
        setTimeout(() => {
          onNavigate(nextNonSkippedIndex);
          setSwipeDirection(null);
          setTimeout(() => setEntryDirection(null), 50);
        }, 200);
      }
    }
  };

  const handleComplete = () => {
    if (canGoNext && nextNonSkippedIndex !== null) {
      onNavigate(nextNonSkippedIndex);
    } else {
      // No more cards - advance to completion screen
      onNavigate(cards.length);
    }
  };

  const handleSkip = () => {
    if (canGoNext && nextNonSkippedIndex !== null) {
      onNavigate(nextNonSkippedIndex);
    }
  };

  const handleSkipExercise = (reason?: string) => {
    if (currentCard && onSkipExercise) {
      onSkipExercise(currentCard.itemId, reason);
      // Navigate to next non-skipped card after marking as skipped
      if (nextNonSkippedIndex !== null) {
        onNavigate(nextNonSkippedIndex);
      }
    }
  };

  const handleOpenSwap = () => {
    if (currentCard?.type === 'exercise_info' && onOpenSwap) {
      onOpenSwap(currentCard.itemId);
    }
  };

  if (!currentCard) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-600 dark:text-slate-400">No cards available</p>
      </div>
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      {/* Progress Bar */}
      <div className="w-full h-1 bg-gray-200 dark:bg-white/10">
        <motion.div
          className="h-full bg-primary-500"
          initial={{ width: 0 }}
          animate={{ width: `${((currentIndex + 1) / cards.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Card Display Area */}
      <div className="flex-1 relative overflow-hidden px-4 py-6 pb-32 md:pb-6">
        <motion.div
          key={currentIndex}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.7}
          onDragEnd={handleDragEnd}
          style={{ opacity, rotate }}
          initial={
            entryDirection === 'right'
              ? { x: 300, opacity: 0 }
              : entryDirection === 'left'
              ? { x: -300, opacity: 0 }
              : { x: 0, opacity: 1 }
          }
          animate={
            swipeDirection === 'left'
              ? { x: -500, opacity: 0 }
              : swipeDirection === 'right'
              ? { x: 500, opacity: 0 }
              : { x: 0, opacity: 1, rotate: 0 }
          }
          transition={{ duration: 0.3 }}
          className="h-full cursor-grab active:cursor-grabbing"
        >
          <WorkoutCardRenderer
            card={currentCard}
            isPlaying={isPlaying}
            ttsEnabled={ttsEnabled}
            restTimerState={restTimerStates[currentCard.id]}
            onSetUpdate={(field, value) => onSetUpdate(currentCard.id, field, value)}
            onComplete={handleComplete}
            onSkip={handleSkip}
            onSkipExercise={handleSkipExercise}
            onOpenSwap={handleOpenSwap}
            onRestTimeUpdate={(seconds) => onRestTimeUpdate(currentCard.id, seconds)}
          />
        </motion.div>
      </div>

      {/* Now Playing Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-white/10 md:relative md:border-t-0 shadow-lg md:shadow-none">
        <div className="px-4 py-3 flex items-center gap-4">
          {/* Cancel Button */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 rounded-full transition hover:bg-gray-100 dark:hover:bg-white/10"
              title="Cancel session"
            >
              <svg
                className="w-5 h-5 text-gray-500 dark:text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* TTS Toggle */}
          {onToggleTts && (
            <button
              onClick={onToggleTts}
              className="p-2 rounded-full transition hover:bg-gray-100 dark:hover:bg-white/10"
              title={ttsEnabled ? 'Mute voice' : 'Unmute voice'}
              data-testid="tts-toggle-button"
            >
              {ttsEnabled ? (
                <svg
                  className="w-5 h-5 text-primary-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9M11 5L6 9H2v6h4l5 4V5z" />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-gray-500 dark:text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>
          )}

          {/* Exercise Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {currentCard.type === 'set'
                ? `${currentCard.exerciseName} - Set ${currentCard.setIndex}`
                : currentCard.type === 'rest'
                ? `Rest - ${currentCard.exerciseName}`
                : currentCard.type === 'exercise_info'
                ? currentCard.exerciseName
                : currentCard.type === 'cues'
                ? `${currentCard.exerciseName} Cues`
                : 'Media'}
            </p>
            <p className="text-xs text-gray-600 dark:text-slate-400">
              {currentIndex + 1} / {cards.length}
            </p>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (prevNonSkippedIndex !== null) {
                  hapticLight();
                  setEntryDirection('left');
                  onNavigate(prevNonSkippedIndex);
                  setTimeout(() => setEntryDirection(null), 50);
                }
              }}
              disabled={!canGoPrev}
              data-testid="workout-prev-button"
              className="p-2 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-white/10"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-slate-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z" />
              </svg>
            </button>

            {/* Play/Pause with Circular Countdown */}
            <div className="relative">
              {/* Circular countdown SVG */}
              <svg
                className="absolute inset-0 w-14 h-14 -m-1 -rotate-90"
                viewBox="0 0 56 56"
              >
                {/* Background circle (subtle) */}
                <circle
                  cx="28"
                  cy="28"
                  r="25"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="text-gray-200 dark:text-white/10"
                />
                {/* Progress circle */}
                {showCountdown && (
                  <circle
                    cx="28"
                    cy="28"
                    r="25"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="text-cyan-400"
                    strokeDasharray={2 * Math.PI * 25}
                    strokeDashoffset={2 * Math.PI * 25 * (1 - progress)}
                    style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                  />
                )}
              </svg>

              <button
                onClick={() => {
                  hapticMedium();
                  onTogglePlay();
                }}
                className="relative w-12 h-12 rounded-full bg-primary-500 hover:bg-primary-600 text-white flex items-center justify-center shadow-lg transition z-10"
              >
                {isPlaying ? (
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 ml-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            </div>

            <button
              onClick={() => {
                if (nextNonSkippedIndex !== null) {
                  hapticLight();
                  setEntryDirection('right');
                  onNavigate(nextNonSkippedIndex);
                  setTimeout(() => setEntryDirection(null), 50);
                }
              }}
              disabled={!canGoNext}
              data-testid="workout-next-button"
              className="p-2 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-white/10"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-slate-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
